import React from 'react';
import { 
  BookOpen,
  VideoCamera,
  Users,
  Rocket,
  Code,
  Headset,
  ChartLine,
  Gear,
  Shield,
  Storefront,
  DeviceMobile
} from '@phosphor-icons/react';
import { 
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Container,
  Button,
  alpha
} from '@mui/material';
import { PageContainer, Content } from '../PageTemplate';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const GradientHero = styled.div`
  background: linear-gradient(
    150deg,
    #ff80b5 0%,
    #9089fc 20%,
    #ff80b5 40%,
    #40c9ff 60%,
    #a5fecb 80%,
    #9089fc 100%
  );
  background-size: 200% 200%;
  animation: gradientMove 15s ease infinite;
  padding: 6rem 2rem 3rem 2rem;
  margin-top: -80px;
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;

  @keyframes gradientMove {
    0% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
    100% { background-position: 0% 50%; }
  }
`;

const SectionCard = styled(Card)`
  height: 100%;
  transition: all 0.3s ease;
  cursor: pointer;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(229, 231, 235, 0.5);
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0,0,0,0.12);
  }

  .MuiTouchRipple-root {
    display: none;
  }
`;

const StyledButton = styled(Button)`
  padding: 8px 16px;
  border-radius: 8px;
  font-weight: 500;
  text-transform: none;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateX(4px);
  }
`;

const helpSections = [
  {
    title: "Documentation",
    description: "Comprehensive guides and tutorials",
    icon: <BookOpen size={32} weight="duotone" />,
    link: "/docs",
    color: "#4f46e5"
  },
  {
    title: "Video Tutorials",
    description: "Step-by-step video guides",
    icon: <VideoCamera size={32} weight="duotone" />,
    link: "/tutorials",
    color: "#7c3aed"
  },
  {
    title: "iOS App",
    description: "Mobile app guides and support",
    icon: <DeviceMobile size={32} weight="duotone" />,
    link: "/ios",
    color: "#000000"
  },
  {
    title: "Community",
    description: "Connect with other users",
    icon: <Users size={32} weight="duotone" />,
    link: "/community",
    color: "#2563eb"
  },
  {
    title: "Getting Started",
    description: "Quick start guides",
    icon: <Rocket size={32} weight="duotone" />,
    link: "/getting-started",
    color: "#059669"
  },
  {
    title: "API Reference",
    description: "Technical documentation",
    icon: <Code size={32} weight="duotone" />,
    link: "/api",
    color: "#dc2626"
  },
  {
    title: "Support",
    description: "Get help from our team",
    icon: <Headset size={32} weight="duotone" />,
    link: "/support",
    color: "#9333ea"
  },
  {
    title: "Analytics",
    description: "Track your performance",
    icon: <ChartLine size={32} weight="duotone" />,
    link: "/analytics",
    color: "#0891b2"
  },
  {
    title: "Settings",
    description: "Configure your account",
    icon: <Gear size={32} weight="duotone" />,
    link: "/settings",
    color: "#4b5563"
  },
  {
    title: "Security",
    description: "Manage your security",
    icon: <Shield size={32} weight="duotone" />,
    link: "/security",
    color: "#16a34a"
  },
  {
    title: "Marketplace",
    description: "Browse integrations",
    icon: <Storefront size={32} weight="duotone" />,
    link: "/marketplace",
    color: "#ea580c"
  }
];

const HelpCenter = () => {
  const navigate = useNavigate();

  const handleSectionClick = (link) => {
    if (link === '/ios') {
      navigate('/resources/help-center/ios');
    } else {
      navigate(link);
    }
  };

  return (
    <PageContainer>
      <GradientHero>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <motion.h1
            style={{
              fontSize: '3.5rem',
              marginBottom: '1.5rem',
              fontWeight: 700,
              position: 'relative',
              zIndex: 2
            }}
          >
            Help Center
          </motion.h1>
          <motion.p
            style={{
              fontSize: '1.25rem',
              maxWidth: '800px',
              margin: '0 auto',
              opacity: 0.9,
              lineHeight: 1.6,
              position: 'relative',
              zIndex: 2
            }}
          >
            Everything you need to succeed with ServTrackr
          </motion.p>
        </motion.div>
      </GradientHero>

      <Content>
        <Container maxWidth="lg">
          <Grid container spacing={3}>
            {helpSections.map((section, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <SectionCard onClick={() => handleSectionClick(section.link)}>
                    <CardContent>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        mb: 2,
                        color: section.color
                      }}>
                        {section.icon}
                        <Typography variant="h6" sx={{ 
                          fontWeight: 'bold',
                          ml: 1
                        }}>
                          {section.title}
                        </Typography>
                      </Box>
                      <Typography color="text.secondary" sx={{ mb: 2 }}>
                        {section.description}
                      </Typography>
                      <StyledButton 
                        variant="text" 
                        sx={{ 
                          color: section.color,
                          backgroundColor: alpha(section.color, 0.08),
                          '&:hover': {
                            backgroundColor: alpha(section.color, 0.12),
                          }
                        }}
                      >
                        Learn More →
                      </StyledButton>
                    </CardContent>
                  </SectionCard>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Content>
    </PageContainer>
  );
};

export default HelpCenter;